/**
 * Ported Python Sugiyama algorithm for swimlane-based layout.
 *
 * Phase 0: Build DAG (cycle removal via DFS)
 * Phase 1: Layer assignment (longest-path layering)
 * Phase 1b: Structural node layer adjustments (fork/join/merge)
 * Phase 2: Column assignment (parallel path detection per lane)
 * Phase 3: Coordinate assignment (X/Y from layers and columns)
 */

import type { IRGraph, IRNode, LayoutResult, LayoutConfig } from '../types';
import { NodeShape } from '../types';

// Layout constants
const COLUMN_WIDTH = 185;
const LANE_PADDING = 40;
const LANE_MIN_WIDTH = 280;
const LAYER_SPACING = 80;
const TOP_MARGIN = 60;
const LANE_HEADER_HEIGHT = 20;
const CONTAINER_HEADER_HEIGHT = 20;

export function swimlaneLayout(graph: IRGraph, config?: LayoutConfig): LayoutResult {
  if (graph.nodes.size === 0) {
    return emptyResult(graph);
  }

  // Core Sugiyama algorithm always computes in canonical TD orientation
  const { dagAdj, dagRev, backEdges } = buildDag(graph);
  const nodeLayer = assignLayers(dagAdj, dagRev, graph);
  adjustStructuralLayers(nodeLayer, graph, dagAdj);
  const maxLayer = Math.max(0, ...nodeLayer.values());
  const { laneColumns, laneMaxCols } = assignColumns(nodeLayer, graph);
  const layerSpacing = config?.ranksep ?? LAYER_SPACING;
  computeCoordinates(graph, nodeLayer, laneColumns, laneMaxCols, maxLayer, layerSpacing);

  // Stamp back-edge metadata before direction transform
  for (const edge of graph.edges) {
    const key = `${edge.source}->${edge.target}`;
    if (backEdges.has(key)) {
      if (!edge.meta) edge.meta = {};
      edge.meta.isBackEdge = true;
    }
  }

  // Apply directional transform as post-processing
  const direction = config?.direction ?? 'TB';
  if (direction !== 'TB' && direction !== 'TD') {
    applyDirectionTransform(graph, direction);
  }

  return buildResult(graph);
}

// ─── Phase 0: Build DAG ──────────────────────────────────────────

function buildDag(graph: IRGraph) {
  const allIds = new Set(graph.nodes.keys());
  const adj = new Map<string, string[]>();
  for (const id of allIds) adj.set(id, []);

  for (const e of graph.edges) {
    if (allIds.has(e.source) && allIds.has(e.target)) {
      adj.get(e.source)!.push(e.target);
    }
  }

  // DFS 3-state coloring for cycle detection
  const color = new Map<string, number>(); // 0=white, 1=gray, 2=black
  for (const id of allIds) color.set(id, 0);
  const backEdges = new Set<string>();

  function dfs(u: string) {
    color.set(u, 1);
    for (const v of adj.get(u) ?? []) {
      if (color.get(v) === 1) {
        backEdges.add(`${u}->${v}`);
      } else if (color.get(v) === 0) {
        dfs(v);
      }
    }
    color.set(u, 2);
  }

  for (const id of allIds) {
    if (color.get(id) === 0) dfs(id);
  }

  // Build DAG adjacency excluding back-edges
  const dagAdj = new Map<string, string[]>();
  const dagRev = new Map<string, string[]>();
  for (const id of allIds) {
    dagAdj.set(id, []);
    dagRev.set(id, []);
  }

  for (const e of graph.edges) {
    const key = `${e.source}->${e.target}`;
    if (allIds.has(e.source) && allIds.has(e.target) && !backEdges.has(key)) {
      dagAdj.get(e.source)!.push(e.target);
      dagRev.get(e.target)!.push(e.source);
    }
  }

  return { dagAdj, dagRev, backEdges };
}

// ─── Phase 1: Layer assignment ───────────────────────────────────

function assignLayers(
  dagAdj: Map<string, string[]>,
  dagRev: Map<string, string[]>,
  graph: IRGraph,
): Map<string, number> {
  const allIds = new Set(graph.nodes.keys());

  // Kahn's topological sort
  const inDeg = new Map<string, number>();
  for (const id of allIds) {
    inDeg.set(id, (dagRev.get(id) ?? []).length);
  }

  const queue: string[] = [];
  for (const [id, deg] of inDeg) {
    if (deg === 0) queue.push(id);
  }

  const topoOrder: string[] = [];
  while (queue.length > 0) {
    const u = queue.shift()!;
    topoOrder.push(u);
    for (const v of dagAdj.get(u) ?? []) {
      const newDeg = (inDeg.get(v) ?? 1) - 1;
      inDeg.set(v, newDeg);
      if (newDeg === 0) queue.push(v);
    }
  }

  // Append unreachable nodes
  for (const id of allIds) {
    if (!topoOrder.includes(id)) topoOrder.push(id);
  }

  // Longest-path layering
  const nodeLayer = new Map<string, number>();
  for (const id of topoOrder) {
    const preds = dagRev.get(id) ?? [];
    if (preds.length === 0) {
      nodeLayer.set(id, 0);
    } else {
      const maxPred = Math.max(...preds.map((p) => nodeLayer.get(p) ?? 0));
      nodeLayer.set(id, maxPred + 1);
    }
  }

  return nodeLayer;
}

// ─── Phase 1b: Structural adjustments ────────────────────────────

function adjustStructuralLayers(
  nodeLayer: Map<string, number>,
  graph: IRGraph,
  dagAdj: Map<string, string[]>,
): void {
  for (const [nid, node] of graph.nodes) {
    if (node.shape === NodeShape.ForkBar || node.shape === NodeShape.JoinBar) {
      const predLayers: number[] = [];
      for (const e of graph.edges) {
        if (e.target === nid && nodeLayer.has(e.source)) {
          predLayers.push(nodeLayer.get(e.source)!);
        }
      }
      if (predLayers.length > 0) {
        const forkLayer = Math.max(...predLayers) + 1;
        nodeLayer.set(nid, forkLayer);
        for (const target of dagAdj.get(nid) ?? []) {
          if (nodeLayer.has(target) && nodeLayer.get(target)! <= forkLayer) {
            nodeLayer.set(target, forkLayer + 1);
          }
        }
      }
    } else if (node.shape === NodeShape.MergeDiamond) {
      const predLayers: number[] = [];
      for (const e of graph.edges) {
        if (e.target === nid && nodeLayer.has(e.source)) {
          predLayers.push(nodeLayer.get(e.source)!);
        }
      }
      if (predLayers.length > 0) {
        nodeLayer.set(nid, Math.max(...predLayers) + 1);
      }
    }
  }
}

// ─── Phase 2: Column assignment ──────────────────────────────────

interface LaneColumns {
  [laneId: string]: Map<number, string[]>;
}

function assignColumns(
  nodeLayer: Map<string, number>,
  graph: IRGraph,
): { laneColumns: LaneColumns; laneMaxCols: Map<string, number> } {
  // Build predecessor map
  const predMap = new Map<string, string[]>();
  for (const e of graph.edges) {
    if (!predMap.has(e.target)) predMap.set(e.target, []);
    predMap.get(e.target)!.push(e.source);
  }

  const laneColumns: LaneColumns = {};
  const laneMaxCols = new Map<string, number>();

  for (const lane of graph.lanes) {
    const layerGroups = new Map<number, string[]>();
    for (const nid of lane.nodeIds) {
      if (graph.nodes.has(nid) && nodeLayer.has(nid)) {
        const layer = nodeLayer.get(nid)!;
        if (!layerGroups.has(layer)) layerGroups.set(layer, []);
        layerGroups.get(layer)!.push(nid);
      }
    }

    // Sort nodes within each layer by predecessor identity
    for (const [, nids] of layerGroups) {
      if (nids.length > 1) {
        nids.sort((a, b) => {
          const predsA = (predMap.get(a) ?? []).sort().join(',');
          const predsB = (predMap.get(b) ?? []).sort().join(',');
          return predsA.localeCompare(predsB);
        });
      }
    }

    laneColumns[lane.id] = layerGroups;
    let maxCols = 1;
    for (const nids of layerGroups.values()) {
      maxCols = Math.max(maxCols, nids.length);
    }
    laneMaxCols.set(lane.id, maxCols);
  }

  return { laneColumns, laneMaxCols };
}

// ─── Phase 3: Coordinate assignment ──────────────────────────────

function computeCoordinates(
  graph: IRGraph,
  nodeLayer: Map<string, number>,
  laneColumns: LaneColumns,
  laneMaxCols: Map<string, number>,
  maxLayer: number,
  layerSpacing: number = LAYER_SPACING,
): void {
  // Layer Y positions
  const layerHeights = new Map<number, number>();
  for (const [nid, layer] of nodeLayer) {
    const node = graph.nodes.get(nid);
    if (node) {
      layerHeights.set(layer, Math.max(layerHeights.get(layer) ?? 0, node.height));
    }
  }

  const layerY = new Map<number, number>();
  let currentY = LANE_HEADER_HEIGHT + TOP_MARGIN;
  for (let layer = 0; layer <= maxLayer; layer++) {
    layerY.set(layer, currentY);
    const nodeH = layerHeights.get(layer) ?? 40;
    currentY += nodeH + layerSpacing;
  }

  const totalHeight = currentY;

  // Lane widths
  const laneWidths = new Map<string, number>();
  for (const lane of graph.lanes) {
    const maxCols = laneMaxCols.get(lane.id) ?? 1;
    if (maxCols <= 1) {
      const nodesInLane = lane.nodeIds
        .map((id) => graph.nodes.get(id))
        .filter((n): n is IRNode => n !== undefined);
      const maxNodeW = Math.max(0, ...nodesInLane.map((n) => n.width));
      laneWidths.set(lane.id, Math.max(LANE_MIN_WIDTH, maxNodeW + LANE_PADDING * 2));
    } else {
      laneWidths.set(lane.id, Math.max(LANE_MIN_WIDTH, maxCols * COLUMN_WIDTH + LANE_PADDING * 2));
    }
  }

  // Position lanes left-to-right
  let currentX = 0;
  for (const lane of graph.lanes) {
    const w = laneWidths.get(lane.id)!;
    lane.x = currentX;
    lane.y = CONTAINER_HEADER_HEIGHT;
    lane.width = w;
    lane.height = totalHeight;
    currentX += w;
  }

  // Position nodes within lanes
  for (const lane of graph.lanes) {
    const lw = laneWidths.get(lane.id)!;
    const layerGroups = laneColumns[lane.id] ?? new Map();

    for (const [layer, nids] of layerGroups) {
      const y = layerY.get(layer) ?? 0;
      const numCols = nids.length;

      if (numCols === 1) {
        const node = graph.nodes.get(nids[0]);
        if (node) {
          node.x = (lw - node.width) / 2;
          node.y = y;
        }
      } else {
        const totalColWidth = numCols * COLUMN_WIDTH;
        const startX = (lw - totalColWidth) / 2;
        for (let col = 0; col < nids.length; col++) {
          const node = graph.nodes.get(nids[col]);
          if (node) {
            const colCenter = startX + col * COLUMN_WIDTH + COLUMN_WIDTH / 2;
            node.x = colCenter - node.width / 2;
            node.y = y;
          }
        }
      }
    }
  }

  // Mark cross-lane edges
  for (const edge of graph.edges) {
    const src = graph.nodes.get(edge.source);
    const tgt = graph.nodes.get(edge.target);
    if (src && tgt) {
      edge.crossLane = src.laneId !== tgt.laneId;
    }
  }
}

// ─── Direction transform ─────────────────────────────────────────

function applyDirectionTransform(
  graph: IRGraph,
  direction: LayoutConfig['direction'],
): void {
  if (direction === 'BT') {
    // Flip Y-axis: find max bottom edge, then mirror all Y coords
    let maxBottom = 0;
    for (const node of graph.nodes.values()) {
      if (node.y !== undefined) {
        maxBottom = Math.max(maxBottom, node.y + node.height);
      }
    }
    for (const node of graph.nodes.values()) {
      if (node.y !== undefined) {
        node.y = maxBottom - node.y - node.height;
      }
    }
    // Lanes stay in place (containers), but flip internal content via node coords
  } else if (direction === 'LR' || direction === 'RL') {
    // Transpose: swap x↔y for nodes, swap x↔y and width↔height for lanes
    for (const node of graph.nodes.values()) {
      if (node.x !== undefined && node.y !== undefined) {
        const tmpX = node.x;
        node.x = node.y;
        node.y = tmpX;
      }
    }

    for (const lane of graph.lanes) {
      const tmpX = lane.x;
      const tmpW = lane.width;
      lane.x = lane.y;
      lane.y = tmpX;
      lane.width = lane.height;
      lane.height = tmpW;
    }

    if (direction === 'RL') {
      // Additionally mirror along the primary (now X) axis
      let maxRight = 0;
      for (const node of graph.nodes.values()) {
        if (node.x !== undefined) {
          maxRight = Math.max(maxRight, node.x + node.width);
        }
      }
      for (const node of graph.nodes.values()) {
        if (node.x !== undefined) {
          node.x = maxRight - node.x - node.width;
        }
      }
      // Mirror lane positions
      let maxLaneRight = 0;
      for (const lane of graph.lanes) {
        maxLaneRight = Math.max(maxLaneRight, lane.x + lane.width);
      }
      for (const lane of graph.lanes) {
        lane.x = maxLaneRight - lane.x - lane.width;
      }
    }
  }
}

// ─── Result building ─────────────────────────────────────────────

function emptyResult(graph: IRGraph): LayoutResult {
  return {
    graph,
    containerX: 234,
    containerY: 230,
    containerWidth: LANE_MIN_WIDTH,
    containerHeight: 200,
  };
}

function buildResult(graph: IRGraph): LayoutResult {
  const totalWidth = graph.lanes.reduce((sum, l) => sum + l.width, 0);
  const sharedHeight = Math.max(200, ...graph.lanes.map((l) => l.height));

  // Equalize lane heights
  for (const lane of graph.lanes) {
    lane.height = sharedHeight;
  }

  return {
    graph,
    containerX: 234,
    containerY: 230,
    containerWidth: totalWidth,
    containerHeight: sharedHeight + CONTAINER_HEADER_HEIGHT,
  };
}
